import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { CentsPerDollarSubCategory } from '../../../../../../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import { unicodeToEmoji } from '../../../../../../../shared/emojiUnicode';
import { useState } from 'react';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';

const colors = [
  'bg-[#71A7FF99]', // Light blue
  'bg-[#8BC5F099]', // Sky blue
  'bg-[#A8EDF199]', // Cyan
  'bg-[#9BF5D499]', // Mint
  'bg-[#A2F8B599]', // Light green
  'bg-[#9AE99299]', // Green
  'bg-[#94ED7E99]', // Lime green
];

const colorsDarker = [
  'bg-[#71A7FFCC]', // Light blue
  'bg-[#8BC5F0CC]', // Sky blue
  'bg-[#A8EDF1CC]', // Cyan
  'bg-[#9BF5D4CC]', // Mint
  'bg-[#A2F8B5CC]', // Light green
  'bg-[#9AE992CC]', // Green
  'bg-[#94ED7ECC]', // Lime green
];

function DollarBreakdown({
  dollarBreakdownData,
  loadingDollarBreakdownData,
  isPreActive,
}: {
  dollarBreakdownData: any[];
  loadingDollarBreakdownData: boolean;
  isPreActive: boolean;
}) {
  const { screenView } = useScreenView();
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex h-full w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Last 30-Day Dollar Breakdown
              </h2>
            </div>
            {!loadingDollarBreakdownData ? (
              <div className="h-[550px] min-h-[550px] w-full p-4">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="relative w-[60%]">
                    <img
                      src="/dollar.svg"
                      alt="Background"
                      className="w-full"
                    />

                    {/* Empty State Overlay */}
                    {(!dollarBreakdownData ||
                      dollarBreakdownData.length === 0 ||
                      dollarBreakdownData.every(
                        (c) => !c?.centsPerDollar || c.centsPerDollar === 0
                      )) && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-[16px] bg-white/50 p-8 text-center backdrop-blur-[2px]">
                        <p className="rounded-lg border border-gray-100 bg-white p-4 text-[16px] font-medium text-graysecondary shadow-sm">
                          Budgeting breakdown data will appear here once
                          deposits are recorded in the last 30 days
                        </p>
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 top-0 flex flex-row">
                      {dollarBreakdownData?.map(
                        (category, index) =>
                          category?.centsPerDollar > 0 && (
                            <div
                              key={index}
                              className={`relative h-full border-r-2 border-white transition-colors duration-200 hover:cursor-pointer ${
                                hoveredCategory === index
                                  ? index <= 5
                                    ? colorsDarker[index]
                                    : colorsDarker[6]
                                  : index <= 5
                                    ? colors[index]
                                    : colors[6]
                              }`}
                              style={{
                                width: `${category?.centsPerDollar}%`,
                              }}
                              onMouseEnter={() => setHoveredCategory(index)}
                              onMouseLeave={() => setHoveredCategory(null)}
                            >
                              {hoveredCategory === index && (
                                <div className="absolute left-1/2 top-[85%] z-10 w-[300px] -translate-x-1/2 -translate-y-full rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                      <span>
                                        {unicodeToEmoji(
                                          category?.emoji_unicode
                                        )}
                                      </span>
                                      <span className="font-semibold">
                                        {category?.name}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      <p>
                                        Cents per Dollar:{' '}
                                        {category?.centsPerDollar}¢
                                      </p>
                                    </div>
                                    {category?.subCategories &&
                                      category?.subCategories?.length > 0 && (
                                        <div className="mt-2">
                                          <p className="text-sm font-semibold">
                                            Sub-categories:
                                          </p>
                                          <ul className="text-sm text-gray-600">
                                            {category?.subCategories.map(
                                              (
                                                sub: CentsPerDollarSubCategory,
                                                idx: number
                                              ) => (
                                                <li key={idx}>
                                                  {unicodeToEmoji(
                                                    sub?.emoji_unicode
                                                  )}{' '}
                                                  {sub?.name}:{' '}
                                                  {sub?.centsPerDollar}¢
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              )}
                              <div className="absolute bottom-0 w-full">
                                <div
                                  className={`absolute ${
                                    index <
                                    Math.ceil(dollarBreakdownData?.length / 2)
                                      ? 'left-1/2'
                                      : 'right-1/2'
                                  } w-[2px] bg-gray-400`}
                                  style={{
                                    height: `${40 + index * index * 5}px`,
                                    bottom: `-${40 + index * index * 5}px`,
                                  }}
                                >
                                  <div
                                    className={`absolute bottom-0 h-[2px] bg-gray-400 ${
                                      index < 2 ? '-left-20' : '-right-20'
                                    }`}
                                    style={{ width: '80px' }}
                                  />
                                  <span
                                    className={`text-md absolute bottom-[-10px] whitespace-nowrap font-[600] text-primary ${
                                      index < 2 ? 'right-[85px]' : 'left-[85px]'
                                    }`}
                                  >
                                    {unicodeToEmoji(category?.emoji_unicode)}{' '}
                                    {category?.name} ({category?.centsPerDollar}
                                    ¢)
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-[550px] max-h-[550px] items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingDollarBreakdownData}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-10 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex h-full w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Last 30-Day Dollar Breakdown
              </h2>
            </div>
            {!loadingDollarBreakdownData ? (
              <div className="h-[500px] min-h-[500px] w-full p-4">
                <div className="flex h-full w-full items-start justify-center">
                  <div className="relative w-[70%]">
                    <img
                      src="/dollar.svg"
                      alt="Background"
                      className="w-full"
                    />

                    {/* Empty State Overlay */}
                    {(!dollarBreakdownData ||
                      dollarBreakdownData.length === 0 ||
                      dollarBreakdownData.every(
                        (c) => !c?.centsPerDollar || c.centsPerDollar === 0
                      )) && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-[16px] bg-white/50 p-8 text-center backdrop-blur-[2px]">
                        <p className="rounded-lg border border-gray-100 bg-white p-4 text-[14px] font-medium text-graysecondary shadow-sm">
                          Budgeting breakdown data will appear here once
                          deposits are recorded in the last 30 days
                        </p>
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 top-0 flex flex-row">
                      {dollarBreakdownData?.map(
                        (category, index) =>
                          category?.centsPerDollar > 0 && (
                            <div
                              key={index}
                              className={`relative h-full border-r-2 border-white transition-colors duration-200 hover:cursor-pointer ${
                                hoveredCategory === index
                                  ? index <= 5
                                    ? colorsDarker[index]
                                    : colorsDarker[6]
                                  : index <= 5
                                    ? colors[index]
                                    : colors[6]
                              }`}
                              style={{
                                width: `${category?.centsPerDollar}%`,
                              }}
                              onMouseEnter={() => setHoveredCategory(index)}
                              onMouseLeave={() => setHoveredCategory(null)}
                            >
                              {hoveredCategory === index && (
                                <div className="absolute left-1/2 top-[85%] z-10 w-[250px] -translate-x-1/2 -translate-y-full rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                      <span>
                                        {unicodeToEmoji(
                                          category?.emoji_unicode
                                        )}
                                      </span>
                                      <span className="font-semibold">
                                        {category?.name}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      <p>
                                        Cents per Dollar:{' '}
                                        {category?.centsPerDollar}¢
                                      </p>
                                    </div>
                                    {category?.subCategories &&
                                      category?.subCategories?.length > 0 && (
                                        <div className="mt-2">
                                          <p className="text-sm font-semibold">
                                            Sub-categories:
                                          </p>
                                          <ul className="text-sm text-gray-600">
                                            {category?.subCategories.map(
                                              (
                                                sub: CentsPerDollarSubCategory,
                                                idx: number
                                              ) => (
                                                <li key={idx}>
                                                  {unicodeToEmoji(
                                                    sub?.emoji_unicode
                                                  )}{' '}
                                                  {sub?.name}:{' '}
                                                  {sub?.centsPerDollar}¢
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              )}
                              <div className="absolute bottom-0 w-full">
                                <div
                                  className={`absolute ${
                                    index <
                                    Math.ceil(dollarBreakdownData?.length / 2)
                                      ? 'left-1/2'
                                      : 'right-1/2'
                                  } w-[2px] bg-gray-400`}
                                  style={{
                                    height: `${20 + (Math.abs(index - 6) + 2) * (Math.abs(index - 6) + 2) * 4}px`,
                                    bottom: `-${20 + (Math.abs(index - 6) + 2) * (Math.abs(index - 6) + 2) * 4}px`,
                                  }}
                                >
                                  <div
                                    className={`absolute bottom-0 h-[2px] bg-gray-400 ${
                                      index < 2
                                        ? '-left-[30px]'
                                        : '-left-[20px]'
                                    }`}
                                    style={{
                                      width: index < 2 ? '30px' : '20px',
                                    }}
                                  />
                                  <span
                                    className={`text-md absolute bottom-[-10px] whitespace-nowrap rounded-[8px] border-[1px] border-bordergray bg-white px-3 py-1 font-[600] text-primary hover:bg-gray-100 ${
                                      index < 2
                                        ? 'right-[35px]'
                                        : 'right-[25px]'
                                    }`}
                                  >
                                    {unicodeToEmoji(category?.emoji_unicode)}{' '}
                                    {category?.name} ({category?.centsPerDollar}
                                    ¢)
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-[500px] max-h-[500px] items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingDollarBreakdownData}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-10 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex h-full w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Last 30-Day Dollar Breakdown
              </h2>
            </div>
            {!loadingDollarBreakdownData ? (
              <div className="h-[300px] min-h-[300px] w-full overflow-hidden p-2">
                <div className="flex h-full w-full items-start justify-center">
                  <div className="relative w-[95%]">
                    <img
                      src="/dollar.svg"
                      alt="Background"
                      className="w-full"
                    />

                    {/* Empty State Overlay */}
                    {(!dollarBreakdownData ||
                      dollarBreakdownData.length === 0 ||
                      dollarBreakdownData.every(
                        (c) => !c?.centsPerDollar || c.centsPerDollar === 0
                      )) && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-[8px] bg-white/50 p-4 text-center backdrop-blur-[2px]">
                        <p className="rounded-lg border border-gray-100 bg-white p-2 text-[12px] font-medium text-graysecondary shadow-sm">
                          Budgeting breakdown data will appear here once
                          deposits are recorded in the last 30 days
                        </p>
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 top-0 flex flex-row">
                      {dollarBreakdownData?.map(
                        (category, index) =>
                          category?.centsPerDollar > 0 && (
                            <div
                              key={index}
                              className={`relative h-full border-r-[1px] border-white transition-colors duration-200 hover:cursor-pointer ${
                                hoveredCategory === index
                                  ? index <= 5
                                    ? colorsDarker[index]
                                    : colorsDarker[6]
                                  : index <= 5
                                    ? colors[index]
                                    : colors[6]
                              }`}
                              style={{
                                width: `${category?.centsPerDollar}%`,
                              }}
                              onMouseEnter={() => setHoveredCategory(index)}
                              onMouseLeave={() => setHoveredCategory(null)}
                            >
                              <div className="absolute bottom-0 w-full">
                                <div
                                  className={`absolute ${
                                    index <
                                    Math.ceil(dollarBreakdownData?.length / 2)
                                      ? 'left-1/2'
                                      : 'right-1/2'
                                  } w-[1px] bg-gray-400`}
                                  style={{
                                    height: `${10 + (Math.abs(index - 6) + 2) * (Math.abs(index - 6) + 2) * 1.8}px`,
                                    bottom: `-${10 + (Math.abs(index - 6) + 2) * (Math.abs(index - 6) + 2) * 1.8}px`,
                                  }}
                                >
                                  <div
                                    className={`absolute bottom-0 h-[1px] bg-gray-400 ${
                                      index < 2
                                        ? '-left-[15px]'
                                        : '-left-[10px]'
                                    }`}
                                    style={{
                                      width: index < 2 ? '15px' : '10px',
                                    }}
                                  />
                                  <span
                                    className={`absolute bottom-[-8px] whitespace-nowrap rounded-[4px] border-[1px] border-bordergray bg-white px-1.5 py-0.5 text-[10px] font-[600] leading-[12px] text-primary ${
                                      index < 2
                                        ? 'right-[20px]'
                                        : 'right-[15px]'
                                    }`}
                                  >
                                    {unicodeToEmoji(category?.emoji_unicode)}{' '}
                                    {category?.name} ({category?.centsPerDollar}
                                    ¢)
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-[300px] max-h-[300px] items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingDollarBreakdownData}
                  size={40}
                  className="my-5 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default DollarBreakdown;
